import styles from "./WeekDayCircles.module.css";

export const WEEK_DAYS = [
  { id: "monday", short: "M", full: "Monday" },
  { id: "tuesday", short: "T", full: "Tuesday" },
  { id: "wednesday", short: "W", full: "Wednesday" },
  { id: "thursday", short: "T", full: "Thursday" },
  { id: "friday", short: "F", full: "Friday" },
  { id: "saturday", short: "S", full: "Saturday" },
  { id: "sunday", short: "S", full: "Sunday" },
];

/**
 * Inline row of seven circular day buttons (M T W T F S S, Monday-first).
 *
 * Two selection modes:
 *   single (default) - `value` is one day id. Clicking the selected day is a
 *     no-op, so one day always stays chosen once the user has picked.
 *   multiple - `value` is an array of day ids and every click reports the day
 *     that was hit, including one already selected, so the caller can toggle.
 *
 * Props:
 *   value:    day id, or an array of day ids when `multiple`.
 *   onChange: called with the day id that was clicked.
 *   multiple: opt into multi-select behaviour.
 *   disabled: disables interaction.
 *   readOnly: renders the selection with no interaction and no disabled
 *     styling — for showing a stored pattern in a table cell.
 *   title:    overrides each button's tooltip (e.g. to explain why it is off).
 */
export const WeekDayCircles = ({
  value,
  onChange,
  multiple = false,
  disabled = false,
  readOnly = false,
  title,
}) => {
  const selectedIds = multiple ? value ?? [] : [value];
  const isInteractive = !disabled && !readOnly;

  return (
    <div
      className={styles.row}
      role={multiple ? "group" : "radiogroup"}
      aria-label="Day of week"
    >
      {WEEK_DAYS.map((day) => {
        const isSelected = selectedIds.includes(day.id);
        const className = [
          styles.day,
          isSelected ? styles.selected : "",
          disabled ? styles.disabled : "",
          readOnly ? styles.readOnly : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <button
            key={day.id}
            type="button"
            {...(multiple
              ? { "aria-pressed": isSelected }
              : { role: "radio", "aria-checked": isSelected })}
            aria-label={day.full}
            title={title ?? day.full}
            className={className}
            disabled={disabled || readOnly}
            onClick={(event) => {
              event.stopPropagation();
              if (!isInteractive) return;
              // Single-select keeps one day chosen; multi-select toggles.
              if (!multiple && isSelected) return;
              onChange?.(day.id);
            }}
          >
            {day.short}
          </button>
        );
      })}
    </div>
  );
};
